import { ref } from 'vue';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserStore } from '@/stores/userStore'; // Store import

export function useProfileSave() {
  const error = ref(null);
  const loading = ref(false);
  const userStore = useUserStore(); // Store'u başlat

  const saveProfile = async ({
    phoneNumber,
    address,
    aboutMe,
    // Yeni serviceAccount parametresini ekleyelim
    serviceAccount,
  }) => {
    loading.value = true;
    error.value = null;

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('User session not found.');

      const userRef = doc(db, 'users', currentUser.uid);

      const userSnapshot = await getDoc(userRef);
      const userData = userSnapshot.exists() ? userSnapshot.data() : {};

      // Mevcut account_number değerini al
      let accountNumber = userData.account_number;
      //console.log('Current Account Number:', accountNumber);

      if (!accountNumber) {
        // Unix timestamp (milisaniye cinsinden) kullanarak unique numara oluştur
        accountNumber = Date.now();
        console.log('New Account Number:', accountNumber);
      }

      // Ana kullanıcı dökümanını güncelle
      const updateData = {
        phoneNumber: phoneNumber || null,
        address: address || null,
        aboutMe: aboutMe || null,
        account_number: accountNumber,
      };

      await setDoc(userRef, updateData, { merge: true });

      // Account koleksiyonunu kontrol et
      const accountCollection = collection(db, 'users', currentUser.uid, 'account');

      // Önce properties dökümanını kontrol et
      const propertiesRef = doc(accountCollection, 'properties');
      const propertiesDoc = await getDoc(propertiesRef);

      // Eğer account collection hiç oluşturulmamışsa
      if (!propertiesDoc.exists()) {
        const batch = writeBatch(db);

        // Sadece temel üyelik yapısını oluştur
        batch.set(propertiesRef, {
          type: 'Member',
          membership_start: new Date(),
          membership_end: null,
        });

        // Pet ve service account'ları da oluştur
        batch.set(doc(accountCollection, 'pet_account'), {
          type: 'Member',
        });

        batch.set(doc(accountCollection, 'service_account'), {
          type: 'Member',
        });

        await batch.commit();
      }

      // Eğer service account güncellemeleri varsa uygula
      if (serviceAccount) {
        const serviceAccountRef = doc(accountCollection, 'service_account');
        await setDoc(
          serviceAccountRef,
          {
            types: serviceAccount.types || [],
            working_hours: serviceAccount.working_hours || null,
            company_name: serviceAccount.company_name || null,
            company_description: serviceAccount.company_description || null,
            type: serviceAccount.type || 'Personal',
          },
          { merge: true },
        );
      }

      // Güncel kullanıcı verilerini ve account bilgilerini al
      const [userDoc, updatedPropertiesDoc, petAccountDoc, serviceAccountDoc] = await Promise.all([
        getDoc(userRef),
        getDoc(propertiesRef),
        getDoc(doc(accountCollection, 'pet_account')),
        getDoc(doc(accountCollection, 'service_account')),
      ]);

      // Tüm verileri birleştir
      const updatedUserData = {
        ...userDoc.data(),
        account: {
          properties: updatedPropertiesDoc.data(),
          pet_account: petAccountDoc.data(),
          service_account: serviceAccountDoc.data(),
        },
      };

      // Store'u güncelle
      userStore.$patch({
        userProfile: updatedUserData,
      });

      // Güncellenmiş veriyi döndür
      return updatedUserData;
    } catch (err) {
      error.value = err.message;
      console.error('Save Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Yeni publishProfile fonksiyonu - bir kullanıcının sadece 1 ilanı olacak şekilde değiştirildi
  const publishProfile = async () => {
    loading.value = true;
    error.value = null;

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('User session not found.');

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data();
      
      // Önce kullanıcının mevcut bir ilanı var mı kontrol et
      let serviceId = userData.publishedService;
      let isNewService = false;
      
      // Gerekli koleksiyonları ve dökümanları getir
      const accountCollection = collection(db, 'users', currentUser.uid, 'account');
      const [propertiesDoc, serviceAccountDoc] = await Promise.all([
        getDoc(doc(accountCollection, 'properties')),
        getDoc(doc(accountCollection, 'service_account')),
      ]);

      if (!propertiesDoc.exists() || !serviceAccountDoc.exists()) {
        throw new Error('Required account information not found');
      }

      const propertiesData = propertiesDoc.data();
      const serviceAccountData = serviceAccountDoc.data();

      // Servis verilerini hazırla
      const serviceData = {
        // user (document) bilgileri
        updatedAt: new Date(),
        userId: currentUser.uid,
        isPublished: true,
        isActive: true,
        displayName: userData.displayName || '',
        photoURL: userData.photoURL || '',
        latitude: userData.latitude || null,
        longitude: userData.longitude || null,
        postalCode: userData.postalCode || '',
        address: userData.address || '',
        aboutMe: userData.aboutMe || '',
        phoneNumber: userData.phoneNumber || '',
        postalTown: userData.postalTown || '',
        email: userData.email || currentUser.email || '',
        
        // account > properties
        accountType: propertiesData.type || 'Member',
        membership_start: propertiesData.membership_start || null,
        
        // account > service_account
        companyName: serviceAccountData.company_name || '',
        companyDescription: serviceAccountData.company_description || '',
        coverImage: serviceAccountData.coverImage || '',
        galleryImages: serviceAccountData.images || [],
        workingHours: serviceAccountData.working_hours || {},
        services: serviceAccountData.types || [],
        type: serviceAccountData.type || 'Personal',
      };

      // Kullanıcının mevcut bir ilanı yoksa yeni oluştur, varsa güncelle
      if (!serviceId) {
        // Yeni ilan oluştur
        isNewService = true;
        const servicesCollection = collection(db, 'services');
        const serviceRef = doc(servicesCollection); // Auto ID oluşturur
        serviceId = serviceRef.id;
        serviceData.createdAt = new Date(); // Sadece yeni ilanlara createdAt ekle
        serviceData.viewCount = 0;
        serviceData.likesCount = 0;
        serviceData.rating = 0;
        serviceData.reviewsCount = 0;
        
        await setDoc(serviceRef, serviceData);
      } else {
        // Mevcut ilanı güncelle
        const serviceRef = doc(db, 'services', serviceId);
        
        // Mevcut ilanı kontrol et
        const serviceDoc = await getDoc(serviceRef);
        if (!serviceDoc.exists()) {
          // Eğer serviceId var ama döküman yoksa (silinmişse) yeni oluştur
          isNewService = true;
          serviceData.createdAt = new Date();
          serviceData.viewCount = 0;
          serviceData.likesCount = 0;
          serviceData.rating = 0;
          serviceData.reviewsCount = 0;
          await setDoc(serviceRef, serviceData);
        } else {
          // Mevcut ilanı güncelle, view count gibi değerleri koru
          const existingService = serviceDoc.data();
          await updateDoc(serviceRef, {
            ...serviceData,
            // Sadece güncellenmemesi gereken alanları koru
            viewCount: existingService.viewCount || 0,
            likesCount: existingService.likesCount || 0,
            rating: existingService.rating || 0,
            reviewsCount: existingService.reviewsCount || 0,
            createdAt: existingService.createdAt // createdAt değişmez
          });
        }
      }

      // User dökümanına servis ID'sini ekle
      await updateDoc(userRef, {
        'publishedService': serviceId,
        'publishedAt': new Date(),
        'isServicePublished': true
      });

      return {
        success: true,
        serviceId: serviceId,
        isNew: isNewService
      };
    } catch (err) {
      error.value = err.message;
      console.error('Publish Error:', err);
      return {
        success: false,
        error: err.message
      };
    } finally {
      loading.value = false;
    }
  };

  // unpublishProfile fonksiyonu ekleyelim
  const unpublishProfile = async () => {
    loading.value = true;
    error.value = null;

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('User session not found.');

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data();
      
      // Kullanıcının yayınlanmış bir ilanı var mı kontrol et
      const serviceId = userData.publishedService;
      
      if (!serviceId) {
        throw new Error('No published service found');
      }

      // İlanın Firestore'da olup olmadığını kontrol et
      const serviceRef = doc(db, 'services', serviceId);
      const serviceDoc = await getDoc(serviceRef);
      
      if (!serviceDoc.exists()) {
        throw new Error('Published service not found in database');
      }
      
      // İlanın yayın durumunu güncelle
      await updateDoc(serviceRef, {
        isPublished: false,
        updatedAt: new Date()
      });
      
      // User dökümanını güncelle
      await updateDoc(userRef, {
        'isServicePublished': false
        // publishedService alanını silmiyoruz, böylece yeniden yayınlamak isterlerse
        // aynı ilan güncellenecek
      });

      return {
        success: true,
        message: 'Service unpublished successfully'
      };
    } catch (err) {
      error.value = err.message;
      console.error('Unpublish Error:', err);
      return {
        success: false,
        error: err.message
      };
    } finally {
      loading.value = false;
    }
  };

  return {
    saveProfile,
    publishProfile,
    unpublishProfile, // Yeni fonksiyonu dışa aktar
    error,
    loading,
  };
}
